import { Component, effect, ElementRef, HostListener, inject, OnInit, ViewChild, AfterViewChecked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { SettingsService } from '@app/_services/settings.service';
import { CacheService } from '@app/_services/cache.service';
import { MeoStore } from '@app/_stores/meo.store';
import { MeoMessageComponent } from '../meo-message/meo-message.component';
import { MeoSuggestionCardComponent } from '../meo-suggestion-card/meo-suggestion-card.component';
import { MeoInputComponent } from '../meo-input/meo-input.component';
import { MeoAudioComponent } from '../meo-audio/meo-audio.component';
import { MeoDebugComponent } from '../meo-debug/meo-debug.component';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { MeoSuggestion, MeoAttachment } from '@app/_interfaces/meo.interface';

@Component({
  selector: 'app-meo-page',
  standalone: true,
  imports: [
    CommonModule,
    MeoMessageComponent,
    MeoSuggestionCardComponent,
    MeoInputComponent,
    MeoAudioComponent,
    MeoDebugComponent,
    SvgComponent,
  ],
  templateUrl: './meo-page.component.html',
  styleUrl: './meo-page.component.scss',
})
export class MeoPageComponent implements OnInit, AfterViewChecked {
  private readonly router = inject(Router);
  readonly ss = inject(SettingsService);
  readonly cc = inject(CacheService);
  readonly meoStore = inject(MeoStore);

  @ViewChild('messageThread') messageThread!: ElementRef<HTMLDivElement>;
  @ViewChild('audioRecorder') audioRecorder!: MeoAudioComponent;
  @ViewChild('inputBar') inputBar!: MeoInputComponent;

  private shouldScrollToBottom = false;
  isVoiceMode = false;
  private autoStartTimer: ReturnType<typeof setTimeout> | null = null;

  /** ARIA live region status text for screen readers. */
  recordingAriaStatus = '';

  constructor() {
    // React to autoStartMic signal — triggers mic recording after panel slide-in
    effect(() => {
      if (this.meoStore.autoStartMic()) {
        // Wait for panel slide-in animation (250ms) + small buffer
        this.autoStartTimer = setTimeout(() => {
          this.autoStartTimer = null;
          if (this.meoStore.autoStartMic()) {
            this.onMicPressed();
            this.meoStore.clearAutoStartMic();
          }
        }, 350);
      } else if (this.autoStartTimer) {
        clearTimeout(this.autoStartTimer);
        this.autoStartTimer = null;
      }
    });
  }

  ngOnInit(): void {
    // AC 8: Fresh open creates new session
    if (!this.meoStore.currentSession()) {
      this.meoStore.startNewSession();
    }
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  onMessageSent(text: string): void {
    this.shouldScrollToBottom = true;
    this.meoStore.sendMessage(text);
  }

  onMessageWithAttachment(event: { text: string; attachment: MeoAttachment }): void {
    this.shouldScrollToBottom = true;
    this.meoStore.sendMessageWithAttachment(event.text, event.attachment);
  }

  onMicPressed(): void {
    if (!this.audioRecorder) return;
    this.isVoiceMode = true;
    this.audioRecorder.startRecording();
    if (this.inputBar) {
      this.inputBar.isRecording.set(true);
    }
    this.meoStore.setRecording(true);
    this.recordingAriaStatus = this.ss.t('meo_recording_started');
  }

  onAudioReady(event: { base64: string; format: string }): void {
    this.isVoiceMode = false;
    this.shouldScrollToBottom = true;
    if (this.inputBar) {
      this.inputBar.isRecording.set(false);
    }
    this.meoStore.setRecording(false);
    this.recordingAriaStatus = this.ss.t('meo_recording_stopped');
    this.meoStore.sendAudio(event.base64, event.format);
    // Clear ARIA status after announcement
    setTimeout(() => { this.recordingAriaStatus = ''; }, 1000);
  }

  onAudioCancelled(): void {
    this.isVoiceMode = false;
    if (this.inputBar) {
      this.inputBar.isRecording.set(false);
    }
    this.meoStore.setRecording(false);
    this.recordingAriaStatus = this.ss.t('meo_recording_stopped');
    // Clear ARIA status after announcement
    setTimeout(() => { this.recordingAriaStatus = ''; }, 1000);
  }

  onConfirmSuggestion(turnIndex: number, suggestionIndex: number): void {
    this.meoStore.confirmSuggestion(turnIndex, suggestionIndex);
  }

  onDismissSuggestion(turnIndex: number, suggestionIndex: number): void {
    this.meoStore.dismissSuggestion(turnIndex, suggestionIndex);
  }

  onDiscoverMore(): void {
    this.onMessageSent(this.ss.t('meo_discoverMorePrompt'));
  }

  onNewSession(): void {
    this.meoStore.startNewSession();
  }

  onGoToHistory(): void {
    this.meoStore.closePanel();
    this.router.navigate(['/meo-history']);
  }

  onToggleDebug(): void {
    this.meoStore.toggleDebug();
  }

  /**
   * Escape key during recording cancels the recording (and stops propagation
   * so PanelComponent.onEscapeKey() does NOT also close the panel).
   * A second Escape press (when not recording) propagates to close the panel.
   */
  @HostListener('keydown.escape', ['$event'])
  onEscape(event: KeyboardEvent): void {
    if (this.meoStore.isRecording()) {
      event.stopPropagation();
      this.audioRecorder.cancelRecording();
      return;
    }
    // Let it propagate to PanelComponent to close panel
  }

  onClose(): void {
    this.meoStore.closePanel();
  }

  /**
   * Get the full suggestion entity for a given turn and suggestion index.
   * Used for preview cards to display entity fields.
   */
  getSuggestionEntity(turnIndex: number, suggestionIndex: number): MeoSuggestion | null {
    const map = this.meoStore.getSuggestionEntitiesMap();
    const entities = map.get(turnIndex);
    if (!entities) return null;
    return entities[suggestionIndex] || null;
  }

  private scrollToBottom(): void {
    if (this.messageThread?.nativeElement) {
      const el = this.messageThread.nativeElement;
      el.scrollTop = el.scrollHeight;
    }
  }
}
